import { useQuery } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { KpiSaldosProveedores } from "@/componentes/kpi/KpiTesoreria"
import { aCentimos, monto } from "@/componentes/tesoreria/dinero"
import {
  EstadoDeCuenta,
  type Saldo,
  type SaldoProveedor,
} from "@/componentes/tesoreria/EstadoCuentaProveedor"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

/**
 * El saldo de cada proveedor frente a la agencia, por moneda, y su estado de cuenta.
 *
 * ⚠ **El saldo se lee por su LADO, con las palabras de tesorería.** «A favor del
 * proveedor» es lo que la agencia le debe: órdenes aprobadas sin pagar y restos de
 * pagos de menos. «Disponible de la empresa» es lo que él le debe a la agencia: lo
 * pagado de más, que se descuenta al pagarle otra orden. El neto es la resta, y un
 * mismo proveedor puede tener las dos cosas a la vez.
 *
 * ⚠ **Nada de esto se teclea: se deriva de las órdenes y sus pagos** (regla 17), así que
 * corregir un pago corrige el saldo. Por moneda y nunca sumado entre monedas.
 */
export function SaldosProveedores() {
  const [abierto, setAbierto] = useState<SaldoProveedor | null>(null)
  const saldos = useQuery({
    queryKey: ["saldos-proveedores"],
    queryFn: () => api.get<SaldoProveedor[]>("/api/v1/saldos-proveedores"),
  })

  if (saldos.isPending) return <p className="py-6">Cargando…</p>
  if (saldos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los saldos de proveedores.
      </p>
    )
  }

  const subtitulo =
    "Lo que se le debe a cada proveedor y lo que cada uno le debe a la agencia, por moneda"
  const documento = (): DocumentoExportable => ({
    titulo: "Saldos de proveedores",
    subtitulo,
    secciones: [
      {
        titulo: "Saldo de cada proveedor",
        columnas: [
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "A favor del proveedor", tipo: "monto" },
          { titulo: "Disponible de la empresa", tipo: "monto" },
          { titulo: "Neto", tipo: "monto" },
        ],
        filas: saldos.data.flatMap((p) =>
          p.saldos.map((s) => ({
            celdas: [
              p.proveedor,
              s.divisa,
              Number(s.a_favor_proveedor),
              Number(s.disponible_empresa),
              Number(s.neto),
            ],
          })),
        ),
      },
    ],
  })

  return (
    <section className="space-y-4">
      {saldos.data.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar titulo="Saldos de proveedores" subtitulo={subtitulo} documento={documento} />
        </div>
      )}
      {saldos.data.length > 0 && <KpiSaldosProveedores saldos={saldos.data} />}
      <Tabla etiqueta="Saldo de cada proveedor">
        <Cabecera>
          <Th>Proveedor</Th>
          <Th derecha>A favor del proveedor</Th>
          <Th derecha>Disponible de la empresa</Th>
          <Th derecha>Neto</Th>
        </Cabecera>
        <Cuerpo>
          {saldos.data.length === 0 && (
            <TablaVacia colSpan={4}>
              Ningún proveedor tiene saldo: no se le debe nada a nadie ni nadie le debe a la
              agencia.
            </TablaVacia>
          )}
          {saldos.data.map((p) => (
            <Fila key={p.proveedor_id} className="cursor-pointer" onClick={() => setAbierto(p)}>
              <Td>{p.proveedor}</Td>
              <Td derecha numero>
                <PorMoneda saldos={p.saldos} campo="a_favor_proveedor" />
              </Td>
              <Td derecha numero>
                <PorMoneda saldos={p.saldos} campo="disponible_empresa" />
              </Td>
              <Td derecha numero>
                <PorMoneda saldos={p.saldos} campo="neto" />
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
      {abierto && (
        <EstadoDeCuenta
          proveedorId={abierto.proveedor_id}
          nombre={abierto.proveedor}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </section>
  )
}

/** Una línea por moneda; sin cifra, un guion. */
function PorMoneda({ saldos, campo }: { saldos: Saldo[]; campo: keyof Omit<Saldo, "divisa"> }) {
  const con = saldos.filter((s) => aCentimos(s[campo]) !== 0n)
  if (con.length === 0) return <>—</>
  return (
    <>
      {con.map((s) => (
        <span key={s.divisa} className="block">
          {monto(s[campo])} {s.divisa}
        </span>
      ))}
    </>
  )
}
